/**
 * Simple RegEx Validator & Tester
 * JustOnlyOneThing Developer Utilities
 * 100% Client-Side • Instant Validation • Zero Latency
 */

(function () {
  'use strict';

  // --- PRESET VALIDATOR TEMPLATES ---
  const PRESETS = {
    email: {
      name: 'Email Address',
      pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
      flags: { g: true, i: true, m: true, s: false, u: false },
      test: `alex.dev@example.com\nsupport-team@corp.subdomain.io\ninvalid-email@\nmissing-domain@.com\ncontact_us+tag@company.co.uk\nplaintext-without-at-sign`,
      replace: '[REDACTED_EMAIL]'
    },
    url: {
      name: 'URL (HTTP/HTTPS)',
      pattern: '^https?:\\/\\/(?:www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b(?:[-a-zA-Z0-9()@:%_\\+.~#?&\\/=]*)$',
      flags: { g: true, i: true, m: true, s: false, u: false },
      test: `https://justonlyonething.com/regex-generator.html\nhttp://subdomain.example.org:8080/api/v1/items?query=financial&sort=desc\nnot-a-valid-url\nftp://invalid-protocol.com`,
      replace: '[EXTERNAL_LINK]'
    },
    phone: {
      name: 'Phone Number (E.164)',
      pattern: '^\\+?[1-9]\\d{1,14}$',
      flags: { g: true, i: false, m: true, s: false, u: false },
      test: `+14155552671\n+919876543210\n+442071838750\n123456\n0000000000\n+0123456789`,
      replace: '[MASKED_PHONE]'
    },
    password: {
      name: 'Strong Password',
      pattern: '^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$',
      flags: { g: true, i: false, m: true, s: false, u: false },
      test: `P@ssw0rd2026!\nSuperSecret#123\nweakpass\nNoSpecialChar123\nalllowercase123!\nNOLOWERCASE123!`,
      replace: '[STRONG_PASS]'
    },
    ipv4: {
      name: 'IPv4 Address',
      pattern: '^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$',
      flags: { g: true, i: false, m: true, s: false, u: false },
      test: `192.168.1.1\n10.0.0.254\n127.0.0.1\n256.100.0.1\n192.168.1\n300.0.0.1`,
      replace: '[IP_ADDRESS]'
    },
    date: {
      name: 'Date (YYYY-MM-DD)',
      pattern: '^\\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\\d|3[01])$',
      flags: { g: true, i: false, m: true, s: false, u: false },
      test: `2026-09-05\n1999-12-31\n2024-02-29\n2026-13-01\n2026-09-32\n05-09-2026`,
      replace: '[VALID_DATE]'
    },
    creditcard: {
      name: 'Credit Card Number',
      pattern: '^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13})$',
      flags: { g: true, i: false, m: true, s: false, u: false },
      test: `4111111111111111\n5500000000000004\n378282246310005\n1234567890123456\n4000`,
      replace: '****-****-****-****'
    },
    hexcolor: {
      name: 'Hex Color (#fff)',
      pattern: '^#?([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$',
      flags: { g: true, i: true, m: true, s: false, u: false },
      test: `#34d399\n#fbbf24\n#fff\n#10B981\n#gggggg\n#12345\n123abc`,
      replace: '[COLOR]'
    },
    username: {
      name: 'Username / Slug',
      pattern: '^[a-zA-Z0-9_-]{3,16}$',
      flags: { g: true, i: false, m: true, s: false, u: false },
      test: `saurabh_01\nalex-dev\nuser99\nab\nthis_username_is_way_too_long_for_limit\ninvalid space`,
      replace: '@$1'
    },
    uuid: {
      name: 'UUID v4',
      pattern: '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$',
      flags: { g: true, i: true, m: true, s: false, u: false },
      test: `4a72d3f1-8c4b-4892-a72e-33f789d98e3b\ne1a23c89-54d2-4318-910a-cb904351670f\nnot-a-valid-uuid-format-here\n4a72d3f1-8c4b-1892-a72e-33f789d98e3b`,
      replace: '[UUID]'
    }
  };

  // --- APPLICATION STATE ---
  const state = {
    pattern: PRESETS.email.pattern,
    flags: { ...PRESETS.email.flags },
    testString: PRESETS.email.test,
    replacePattern: PRESETS.email.replace,
    compiledRegex: null,
    isValid: true,
    errorMessage: '',
    matches: [],
    activeCodeTab: 'js'
  };

  // --- GET FLAGS STRING ---
  function getFlagsString() {
    let f = '';
    if (state.flags.g) f += 'g';
    if (state.flags.i) f += 'i';
    if (state.flags.m) f += 'm';
    if (state.flags.s) f += 's';
    if (state.flags.u) f += 'u';
    return f;
  }

  // --- VALIDATE & COMPILE REGEX ---
  function validateAndRun() {
    const flagsStr = getFlagsString();
    state.isValid = true;
    state.errorMessage = '';
    state.matches = [];

    if (!state.pattern) {
      state.compiledRegex = null;
      renderStatus('empty');
      renderMatches([]);
      renderGroupsTable([]);
      renderReplacement();
      updateCodeSnippets();
      return;
    }

    try {
      state.compiledRegex = new RegExp(state.pattern, flagsStr);
    } catch (err) {
      state.isValid = false;
      state.errorMessage = err.message;
      state.compiledRegex = null;
      renderStatus('error', err.message);
      renderMatches([]);
      renderGroupsTable([]);
      renderReplacement();
      updateCodeSnippets();
      return;
    }

    // Execute matching against test string
    executeMatches();
    updateCodeSnippets();
  }

  // --- EXECUTE MATCHES AGAINST TEST STRING ---
  function executeMatches() {
    const text = state.testString || '';
    const re = state.compiledRegex;
    const matches = [];

    if (!re || !text) {
      renderStatus('valid-nomatch', 0, 0);
      renderMatches([]);
      renderGroupsTable([]);
      renderReplacement();
      return;
    }

    const startTime = performance.now();

    try {
      re.lastIndex = 0;
      if (re.global) {
        let m;
        while ((m = re.exec(text)) !== null) {
          matches.push({
            index: m.index,
            text: m[0],
            length: m[0].length,
            groups: m.groups ? { ...m.groups } : null,
            submatches: m.slice(1)
          });
          // Prevent infinite loop on empty match
          if (m[0].length === 0) {
            re.lastIndex++;
          }
          if (matches.length > 500) break;
        }
      } else {
        const m = re.exec(text);
        if (m) {
          matches.push({
            index: m.index,
            text: m[0],
            length: m[0].length,
            groups: m.groups ? { ...m.groups } : null,
            submatches: m.slice(1)
          });
        }
      }
    } catch (e) {
      renderStatus('error', e.message);
      return;
    }

    const elapsed = (performance.now() - startTime).toFixed(2);
    state.matches = matches;

    if (matches.length > 0) {
      renderStatus('valid-match', matches.length, elapsed);
    } else {
      renderStatus('valid-nomatch', 0, elapsed);
    }

    renderMatches(matches);
    renderGroupsTable(matches);
    renderReplacement();
  }

  // --- RENDER STATUS BADGE ---
  function renderStatus(type, count = 0, elapsed = 0) {
    const badge = document.getElementById('rvStatusBadge');
    if (!badge) return;

    if (type === 'empty') {
      badge.className = 'rv-badge valid-nomatch';
      badge.innerHTML = '<span>ℹ️</span> Enter a regex pattern above';
    } else if (type === 'error') {
      badge.className = 'rv-badge invalid';
      badge.innerHTML = `<span>❌</span> Invalid RegEx: ${escapeHtml(state.errorMessage)}`;
    } else if (type === 'valid-match') {
      badge.className = 'rv-badge valid-match';
      badge.innerHTML = `<span>✅</span> Valid RegEx • ${count} ${count === 1 ? 'Match' : 'Matches'} Found (${elapsed}ms)`;
    } else {
      badge.className = 'rv-badge valid-nomatch';
      badge.innerHTML = `<span>⚠️</span> Valid RegEx • 0 Matches in test string (${elapsed}ms)`;
    }
  }

  // --- RENDER HIGHLIGHTED MATCHES ---
  function renderMatches(matches) {
    const box = document.getElementById('rvHighlightBox');
    const text = state.testString || '';
    if (!box) return;

    if (!text) {
      box.innerHTML = '<span style="color:var(--text-muted);font-style:italic;">Test string output will highlight here...</span>';
      return;
    }

    if (!matches || matches.length === 0) {
      box.textContent = text;
      return;
    }

    let html = '';
    let cur = 0;

    matches.forEach((m, idx) => {
      if (m.index > cur) {
        html += escapeHtml(text.slice(cur, m.index));
      }
      html += `<mark class="rv-match" title="Match #${idx + 1} at [${m.index}:${m.index + m.length}]">${escapeHtml(m.text)}</mark>`;
      cur = m.index + m.length;
    });

    if (cur < text.length) {
      html += escapeHtml(text.slice(cur));
    }

    box.innerHTML = html;
  }

  // --- RENDER CAPTURE GROUPS TABLE ---
  function renderGroupsTable(matches) {
    const tbody = document.getElementById('rvGroupsTableBody');
    if (!tbody) return;

    if (!matches || matches.length === 0) {
      tbody.innerHTML = '<tr><td colspan="4" style="text-align:center; color:var(--text-muted); padding:1rem;">No matching captures to display.</td></tr>';
      return;
    }

    let html = '';
    matches.slice(0, 15).forEach((m, idx) => {
      // Check named groups
      if (m.groups && Object.keys(m.groups).length > 0) {
        for (const [name, val] of Object.entries(m.groups)) {
          html += `
            <tr>
              <td><strong>#${idx + 1}</strong></td>
              <td><code>[${m.index} : ${m.index + m.length}]</code></td>
              <td><code>&lt;${escapeHtml(name)}&gt;</code></td>
              <td><code>${escapeHtml(val !== undefined ? val : '')}</code></td>
            </tr>
          `;
        }
      } else if (m.submatches && m.submatches.length > 0) {
        m.submatches.forEach((sub, subIdx) => {
          html += `
            <tr>
              <td><strong>#${idx + 1}</strong></td>
              <td><code>[${m.index} : ${m.index + m.length}]</code></td>
              <td>Group (${subIdx + 1})</td>
              <td><code>${escapeHtml(sub !== undefined ? sub : '')}</code></td>
            </tr>
          `;
        });
      } else {
        html += `
          <tr>
            <td><strong>#${idx + 1}</strong></td>
            <td><code>[${m.index} : ${m.index + m.length}]</code></td>
            <td>Full Match</td>
            <td><code>${escapeHtml(m.text)}</code></td>
          </tr>
        `;
      }
    });

    if (matches.length > 15) {
      html += `<tr><td colspan="4" style="text-align:center; color:var(--text-muted); font-size:0.75rem;">... and ${matches.length - 15} more matches</td></tr>`;
    }

    tbody.innerHTML = html;
  }

  // --- RENDER SUBSTITUTION PREVIEW ---
  function renderReplacement() {
    const resBox = document.getElementById('rvReplaceResult');
    if (!resBox) return;

    if (!state.compiledRegex || !state.testString) {
      resBox.textContent = state.testString || '';
      return;
    }

    try {
      const replaced = state.testString.replace(state.compiledRegex, state.replacePattern);
      resBox.textContent = replaced;
    } catch (e) {
      resBox.textContent = 'Error in substitution: ' + e.message;
    }
  }

  // --- POLYGLOT CODE SNIPPETS ---
  function updateCodeSnippets() {
    const pre = document.getElementById('rvCodePre');
    if (!pre) return;

    const pat = state.pattern || '';
    const flags = getFlagsString();
    const lang = state.activeCodeTab;
    const escapedForStr = pat.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    let snippet = '';

    switch (lang) {
      case 'js':
        snippet = `// JavaScript (ES6+ / Node.js)
const pattern = /${pat}/${flags};
const text = "Sample string to validate";

// 1. Boolean validation check:
const isValid = pattern.test(text);
console.log("Is Valid:", isValid);

// 2. Extract matches & groups:
const matches = [...text.matchAll(pattern)];
matches.forEach((match, i) => {
  console.log(\`Match \${i + 1}: \${match[0]}\`);
  if (match.groups) console.log("Named groups:", match.groups);
});`;
        break;

      case 'python':
        const pyFlags = [];
        if (flags.includes('i')) pyFlags.push('re.IGNORECASE');
        if (flags.includes('m')) pyFlags.push('re.MULTILINE');
        if (flags.includes('s')) pyFlags.push('re.DOTALL');
        const flagStr = pyFlags.length > 0 ? ', ' + pyFlags.join(' | ') : '';

        snippet = `# Python 3
import re

pattern = r"${pat}"
text = "Sample string to validate"

# 1. Direct validation:
if re.search(pattern, text${flagStr}):
    print("Match found!")

# 2. Extract all occurrences:
for m in re.finditer(pattern, text${flagStr}):
    print(f"Matched '{m.group(0)}' at [{m.start()}:{m.end()}]")`;
        break;

      case 'go':
        snippet = `// Golang
package main

import (
\t"fmt"
\t"regexp"
)

func main() {
\tpattern := \`${pat}\`
\tre := regexp.MustCompile(pattern)
\ttext := "Sample string to validate"

\t// 1. Validate match
\tif re.MatchString(text) {
\t\tfmt.Println("Text is valid!")
\t}

\t// 2. Extract submatches
\tmatches := re.FindAllString(text, -1)
\tfmt.Printf("Matches found: %v\\n", matches)
}`;
        break;

      case 'java':
        snippet = `// Java
import java.util.regex.Pattern;
import java.util.regex.Matcher;

public class RegexValidator {
    public static void main(String[] args) {
        String regex = "${escapedForStr}";
        Pattern pattern = Pattern.compile(regex);
        String text = "Sample string to validate";

        Matcher matcher = pattern.matcher(text);
        if (matcher.find()) {
            System.out.println("Match found: " + matcher.group(0));
        } else {
            System.out.println("No match found.");
        }
    }
}`;
        break;

      case 'csharp':
        snippet = `// C# (.NET 8+)
using System;
using System.Text.RegularExpressions;

class Program {
    static void Main() {
        string pattern = @"${pat}";
        string input = "Sample string to validate";

        bool isValid = Regex.IsMatch(input, pattern);
        Console.WriteLine($"Is Valid: {isValid}");

        MatchCollection matches = Regex.Matches(input, pattern);
        foreach (Match m in matches) {
            Console.WriteLine($"Match: {m.Value} at index {m.Index}");
        }
    }
}`;
        break;

      case 'php':
        snippet = `<?php
// PHP
$pattern = '/${pat}/${flags}';
$text = "Sample string to validate";

// 1. Test validation
if (preg_match($pattern, $text)) {
    echo "Pattern matches string!\\n";
}

// 2. Extract all
if (preg_match_all($pattern, $text, $matches)) {
    print_r($matches[0]);
}`;
        break;

      case 'rust':
        snippet = `// Rust (add 'regex = "1"' to Cargo.toml)
use regex::Regex;

fn main() {
    let re = Regex::new(r"${pat}").unwrap();
    let text = "Sample string to validate";

    if re.is_match(text) {
        println!("Valid match found!");
    }

    for cap in re.captures_iter(text) {
        println!("Match: {}", &cap[0]);
    }
}`;
        break;

      case 'ruby':
        snippet = `# Ruby
pattern = /${pat}/${flags}
text = "Sample string to validate"

if text =~ pattern
  puts "Valid match!"
end

text.scan(pattern) do |match|
  puts "Match: #{match}"
end`;
        break;
    }

    pre.textContent = snippet;
  }

  // --- PRESET LOADER ---
  function loadPreset(key) {
    const p = PRESETS[key];
    if (!p) return;

    state.pattern = p.pattern;
    state.flags = { ...p.flags };
    state.testString = p.test;
    state.replacePattern = p.replace;

    const patInput = document.getElementById('rvPatternInput');
    if (patInput) patInput.value = p.pattern;

    const testArea = document.getElementById('rvTextArea');
    if (testArea) testArea.value = p.test;

    const repInput = document.getElementById('rvReplaceInput');
    if (repInput) repInput.value = p.replace;

    syncFlagsUI();
    validateAndRun();
    updateCharAndLineCounts();
  }

  // --- SYNC FLAGS UI ---
  function syncFlagsUI() {
    ['g', 'i', 'm', 's', 'u'].forEach(flag => {
      const pill = document.querySelector(`.rv-flag-pill[data-flag="${flag}"]`);
      if (pill) {
        if (state.flags[flag]) pill.classList.add('active');
        else pill.classList.remove('active');
      }
    });
  }

  // --- UPDATE COUNTERS ---
  function updateCharAndLineCounts() {
    const counter = document.getElementById('rvCharCount');
    if (!counter) return;
    const str = state.testString || '';
    const chars = str.length;
    const lines = str ? str.split('\n').length : 0;
    counter.textContent = `${lines} ${lines === 1 ? 'line' : 'lines'}, ${chars} chars`;
  }

  // --- COPY TO CLIPBOARD ---
  function copyText(str, btn, successMsg = 'Copied!') {
    navigator.clipboard.writeText(str).then(() => {
      const orig = btn.innerHTML;
      btn.innerHTML = `<span>✓</span> ${successMsg}`;
      btn.classList.add('success');
      setTimeout(() => {
        btn.innerHTML = orig;
        btn.classList.remove('success');
      }, 1600);
    }).catch(() => {
      alert('Could not copy to clipboard.');
    });
  }

  // --- ESCAPE HTML ---
  function escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // --- INITIALIZATION ---
  document.addEventListener('DOMContentLoaded', () => {
    const patInput = document.getElementById('rvPatternInput');
    const testArea = document.getElementById('rvTextArea');
    const repInput = document.getElementById('rvReplaceInput');

    // 1. Bind Pattern Input
    if (patInput) {
      patInput.value = state.pattern;
      patInput.addEventListener('input', () => {
        state.pattern = patInput.value;
        validateAndRun();
      });
    }

    // 2. Bind Flags Pills
    document.querySelectorAll('.rv-flag-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        const flag = pill.getAttribute('data-flag');
        state.flags[flag] = !state.flags[flag];
        syncFlagsUI();
        validateAndRun();
        window.Analytics?.trackToolAction('regex_validator', 'toggle_flag', { flag, enabled: state.flags[flag] });
      });
    });

    // 3. Bind Test Area
    if (testArea) {
      testArea.value = state.testString;
      updateCharAndLineCounts();
      testArea.addEventListener('input', () => {
        state.testString = testArea.value;
        updateCharAndLineCounts();
        validateAndRun();
      });
    }

    // 4. Bind Replace Input
    if (repInput) {
      repInput.value = state.replacePattern;
      repInput.addEventListener('input', () => {
        state.replacePattern = repInput.value;
        renderReplacement();
      });
    }

    // 5. Bind Presets
    document.querySelectorAll('.rv-preset-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const key = chip.getAttribute('data-preset');
        loadPreset(key);
        window.Analytics?.track('preset_select', { tool_name: 'regex_validator', preset_name: key });
      });
    });

    // 6. Action Buttons
    const btnClear = document.getElementById('rvBtnClear');
    if (btnClear) {
      btnClear.addEventListener('click', () => {
        state.pattern = '';
        if (patInput) patInput.value = '';
        validateAndRun();
      });
    }

    const btnClearTest = document.getElementById('rvBtnClearTest');
    if (btnClearTest) {
      btnClearTest.addEventListener('click', () => {
        state.testString = '';
        if (testArea) testArea.value = '';
        updateCharAndLineCounts();
        validateAndRun();
      });
    }

    const btnCopyRegex = document.getElementById('rvBtnCopyRegex');
    if (btnCopyRegex) {
      btnCopyRegex.addEventListener('click', () => {
        copyText(state.pattern, btnCopyRegex, 'RegEx Copied!');
      });
    }

    const btnCopyFull = document.getElementById('rvBtnCopyFull');
    if (btnCopyFull) {
      btnCopyFull.addEventListener('click', () => {
        const full = `/${state.pattern}/${getFlagsString()}`;
        copyText(full, btnCopyFull, 'Pattern Copied!');
      });
    }

    const btnCopyLiteral = document.getElementById('rvBtnCopyLiteral');
    if (btnCopyLiteral) {
      btnCopyLiteral.addEventListener('click', () => {
        const escaped = state.pattern.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        copyText(`"${escaped}"`, btnCopyLiteral, 'Literal Copied!');
      });
    }

    const btnCopyReplaced = document.getElementById('rvBtnCopyReplaced');
    const replaceRes = document.getElementById('rvReplaceResult');
    if (btnCopyReplaced && replaceRes) {
      btnCopyReplaced.addEventListener('click', () => {
        copyText(replaceRes.textContent, btnCopyReplaced, 'Replaced Text Copied!');
      });
    }

    const btnShare = document.getElementById('rvBtnShare');
    if (btnShare) {
      btnShare.addEventListener('click', () => {
        const params = new URLSearchParams();
        params.set('p', state.pattern);
        params.set('f', getFlagsString());
        window.location.hash = params.toString();
        copyText(window.location.href, btnShare, 'Share URL Copied!');
      });
    }

    // 7. Polyglot Code Tabs
    document.querySelectorAll('.rv-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        document.querySelectorAll('.rv-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        state.activeCodeTab = tab.getAttribute('data-lang');
        updateCodeSnippets();
      });
    });

    const btnCopyCode = document.getElementById('rvBtnCopyCode');
    const codePre = document.getElementById('rvCodePre');
    if (btnCopyCode && codePre) {
      btnCopyCode.addEventListener('click', () => {
        copyText(codePre.textContent, btnCopyCode, 'Code Copied!');
      });
    }

    // 8. Restore from URL Hash or initial load
    if (window.location.hash && window.location.hash.length > 1) {
      try {
        const params = new URLSearchParams(window.location.hash.slice(1));
        if (params.has('p')) {
          state.pattern = params.get('p') || '';
          if (patInput) patInput.value = state.pattern;
        }
        if (params.has('f')) {
          const f = params.get('f') || '';
          state.flags.g = f.includes('g');
          state.flags.i = f.includes('i');
          state.flags.m = f.includes('m');
          state.flags.s = f.includes('s');
          state.flags.u = f.includes('u');
        }
      } catch (e) {}
    }

    syncFlagsUI();
    validateAndRun();
  });

})();
